export type WbsLibraryTemplateComponent = {
  resourceClassId?: string;
  resourceClassCode?: string;
  resourceClassName?: string;
  itemId?: string;
  itemCode?: string;
  itemName?: string;
  description?: string;
  uom?: string;
  rateBasis?: string;
  headcount?: number;
  hours?: number;
  ownershipMode?: string;
  componentKind?: string;
  quantity?: number;
  duration?: number;
  durationUom?: string;
  rate?: number;
  amount?: number;
  sortOrder?: number;
};

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

export function parseTemplateComponents(meta: unknown): WbsLibraryTemplateComponent[] {
  if (!meta) return [];
  let obj: Record<string, unknown> | null = null;
  if (typeof meta === 'string') {
    try {
      obj = JSON.parse(meta) as Record<string, unknown>;
    } catch {
      return [];
    }
  } else if (typeof meta === 'object') {
    obj = meta as Record<string, unknown>;
  }
  if (!obj || !Array.isArray(obj.components)) return [];
  return obj.components
    .map((c: any, i: number) => {
      const itemId = String(c?.itemId || c?.item_id || '') || undefined;
      const itemCode = String(c?.itemCode || c?.item_code || '') || undefined;
      const resourceClassId =
        String(c?.resourceClassId || c?.resource_class_id || itemId || '') || undefined;
      const resourceClassCode =
        String(c?.resourceClassCode || c?.resource_class_code || itemCode || '') || undefined;
      if (!resourceClassId && !itemId && !resourceClassCode && !itemCode) return null;
      const rate = Number(c?.rate ?? 0);
      const headcount = Number(c?.headcount ?? 1);
      const hours = Number(c?.hours ?? 0);
      const quantity = Number(c?.quantity ?? 1);
      const duration = Number(c?.duration ?? 0);
      const amount =
        Number(c?.amount) ||
        (hours > 0 || headcount !== 1
          ? round2(headcount * hours * rate)
          : duration > 0
            ? round2(quantity * duration * rate)
            : round2(quantity * rate));
      return {
        resourceClassId,
        resourceClassCode,
        resourceClassName: String(
          c?.resourceClassName || c?.resource_class_name || c?.itemName || resourceClassCode || ''
        ),
        itemId,
        itemCode,
        itemName: String(c?.itemName || c?.item_name || '') || undefined,
        description: c?.description ? String(c.description) : undefined,
        uom: c?.uom ? String(c.uom) : undefined,
        rateBasis: c?.rateBasis || c?.rate_basis || 'NORMAL',
        headcount,
        hours,
        ownershipMode: c?.ownershipMode || c?.ownership_mode || 'RENTED',
        componentKind: c?.componentKind || c?.component_kind || 'BASE',
        quantity,
        duration,
        durationUom: c?.durationUom || c?.duration_uom || 'DAY',
        rate,
        amount,
        sortOrder: Number(c?.sortOrder ?? c?.sort_order ?? (i + 1) * 100),
      } satisfies WbsLibraryTemplateComponent;
    })
    .filter(Boolean) as WbsLibraryTemplateComponent[];
}

export function componentTotal(components: WbsLibraryTemplateComponent[]): number {
  return round2(components.reduce((sum, c) => sum + (Number(c.amount) || 0), 0));
}

export function resourceSummary(row: {
  meta_json?: unknown;
  source_ref_label?: string | null;
  resource_kind?: string;
}): string | null {
  const components = parseTemplateComponents(row.meta_json);
  if (components.length > 1) {
    const noun = String(row.resource_kind || '').toUpperCase() === 'MATERIAL' ? 'materials' : 'resources';
    return `${components.length} ${noun}`;
  }
  if (components.length === 1) {
    return (
      components[0].itemCode ||
      components[0].resourceClassCode ||
      components[0].description ||
      null
    );
  }
  return row.source_ref_label || null;
}

export function enrichComponentsForKind(
  resourceKind: string,
  rows: Array<Record<string, unknown>>,
  classById: Map<string, { code: string; name: string }>
): WbsLibraryTemplateComponent[] {
  const kind = resourceKind.toUpperCase();
  return rows
    .map((r, i) => {
      const rate = Number(r.rate ?? 0);
      if (kind === 'MATERIAL') {
        const itemId = String(r.itemId || r.resourceClassId || '');
        const itemCode = String(r.itemCode || '');
        const itemName = String(r.itemName || r.description || itemCode);
        if (!itemId && !itemCode && !r.description) return null;
        const quantity = Number(r.quantity ?? 1);
        return {
          itemId: itemId || undefined,
          itemCode: itemCode || undefined,
          itemName,
          resourceClassId: itemId || undefined,
          resourceClassCode: itemCode || undefined,
          resourceClassName: itemName,
          description: r.description ? String(r.description) : itemName,
          componentKind: String(r.componentKind || 'BASE'),
          quantity,
          uom: r.uom ? String(r.uom) : undefined,
          rate,
          amount: round2(quantity * rate),
          sortOrder: (i + 1) * 100,
        };
      }
      const resourceClassId = String(r.resourceClassId || '');
      const cls = classById.get(resourceClassId);
      if (!resourceClassId || !cls) return null;
      if (kind === 'LABOUR') {
        const headcount = Number(r.headcount ?? 1);
        const hours = Number(r.hours ?? 0);
        return {
          resourceClassId,
          resourceClassCode: cls.code,
          resourceClassName: cls.name,
          rateBasis: String(r.rateBasis || 'NORMAL'),
          headcount,
          hours,
          rate,
          amount: round2(headcount * hours * rate),
          sortOrder: (i + 1) * 100,
        };
      }
      const quantity = Number(r.quantity ?? 1);
      const duration = Number(r.duration ?? 0);
      return {
        resourceClassId,
        resourceClassCode: cls.code,
        resourceClassName: cls.name,
        ownershipMode: String(r.ownershipMode || 'RENTED'),
        componentKind: String(r.componentKind || 'BASE'),
        quantity,
        duration,
        durationUom: String(r.durationUom || 'DAY'),
        rate,
        amount: round2(quantity * duration * rate),
        sortOrder: (i + 1) * 100,
      };
    })
    .filter(Boolean) as WbsLibraryTemplateComponent[];
}
